import { localPoint } from "@visx/event";
import { InfraEventPassengerCount } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/types";
import { CAPACITY_PER_BUS } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/constants";

// Data accessors
export const getDate = (d: InfraEventPassengerCount): Date => d.date;
export const getBusCount = (d: InfraEventPassengerCount): number =>
  Math.ceil(d.passengers / CAPACITY_PER_BUS);
export const getValue = (d: InfraEventPassengerCount): number => d.passengers;

// Time formatting utility
export const formatTime = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
};

// Find closest data point for tooltip
export const findClosestDataPoint = (
  data: InfraEventPassengerCount[],
  targetDate: Date,
): InfraEventPassengerCount => {
  if (data.length === 0) {
    throw new Error("Data array cannot be empty");
  }

  return data.reduce((closest, current) => {
    const currentDistance = Math.abs(
      getDate(current).getTime() - targetDate.getTime(),
    );
    const closestDistance = Math.abs(
      getDate(closest).getTime() - targetDate.getTime(),
    );
    return currentDistance < closestDistance ? current : closest;
  });
};

// Handle mouse events for tooltip
export const getMousePosition = (event: React.MouseEvent<SVGElement>) =>
  localPoint(event) || { x: 0, y: 0 };

export const computeNiceStep = (
  maxValue: number,
  desiredTickCount: number = 5,
): { step: number; niceMax: number } => {
  const safeMax = Math.max(0, maxValue);
  if (safeMax === 0) {
    return { step: 1, niceMax: 1 };
  }
  const rawStep = safeMax / Math.max(1, desiredTickCount);
  const pow10 = 10 ** Math.floor(Math.log10(rawStep));
  const fraction = rawStep / pow10;
  let niceFraction: number;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;
  const step = niceFraction * pow10;
  const niceMax = Math.max(step, Math.ceil(safeMax / step) * step);

  return { step, niceMax };
};

// Area chart markers interface
export interface AreaChartMarkers {
  currentTimeMarker: InfraEventPassengerCount | null;
  maxPassengersMarker: InfraEventPassengerCount | null;
}

// Get area chart markers for current time and maximum passengers
export const getAreaChartMarkers = (
  chosenStretchData: InfraEventPassengerCount[],
  totalEventData: InfraEventPassengerCount[],
): AreaChartMarkers => {
  const currentTime = new Date();

  const markerData =
    chosenStretchData.length > 0 ? chosenStretchData : totalEventData;
  if (markerData.length === 0) {
    return { currentTimeMarker: null, maxPassengersMarker: null };
  }

  // get the data point before current time
  const currentTimeMarker = markerData.reduce<InfraEventPassengerCount | null>(
    (best, point) => {
      if (getDate(point).getTime() > currentTime.getTime()) return best;
      if (!best || getDate(point).getTime() > getDate(best).getTime())
        return point;
      return best;
    },
    null,
  );

  // Find the data point with maximum number of passengers
  // Filter out data points with null passengers first
  const dataWithPassengers = markerData.filter(
    (d) => d.passengers !== null && d.passengers !== undefined,
  );

  // No max when all passenger counts are zero
  const hasPassengers = !dataWithPassengers
    .map((d) => d.passengers)
    .every((p) => p === 0);

  let maxPassengersMarker: InfraEventPassengerCount | null = null;
  if (dataWithPassengers.length > 0 && hasPassengers) {
    maxPassengersMarker = dataWithPassengers.reduce((maxPoint, current) => {
      if (current.passengers === null || maxPoint.passengers === null) {
        return maxPoint;
      }
      return current.passengers > maxPoint.passengers ? current : maxPoint;
    });
  }

  return {
    currentTimeMarker,
    maxPassengersMarker,
  };
};
